import React, { useState, useEffect } from 'react';
import { Container, Button, Table, Form, Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FaPlus, FaEdit, FaTrash } from 'react-icons/fa';
import { toast } from 'react-toastify';
import categoryService from '../services/categoryService';
import { getCategoryTypeLabel } from '../constants/categoryTypes';
import CategoryTypeSelect from '../components/CategoryTypeSelect';
import ConfirmModal from '../components/ConfirmModal';

const Categories = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [filters, setFilters] = useState({
    id: '',
    name: '',
    type: ''
  });

  const loadCategories = async () => {
    try {
      setLoading(true);
      const data = await categoryService.getAll();
      setCategories(data);
      setFilteredCategories(data);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
      toast.error('Erro ao carregar categorias. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    const filtered = categories.filter(category => {
      const matchesId = !filters.id || category.id.toString().includes(filters.id);
      const matchesName = !filters.name || category.name.toLowerCase().includes(filters.name.toLowerCase());
      const matchesType = !filters.type || category.type === filters.type;
      return matchesId && matchesName && matchesType;
    });
    setFilteredCategories(filtered);
  }, [categories, filters]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      id: '',
      name: '',
      type: ''
    });
  };

  const handleEdit = (id) => {
    navigate(`/categories/${id}`);
  };

  const handleDelete = (id) => {
    setCategoryToDelete(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await categoryService.delete(categoryToDelete);
      toast.success('Categoria excluída com sucesso!');
      loadCategories();
    } catch (error) {
      console.error('Erro ao excluir categoria:', error);
      toast.error('Erro ao excluir categoria. Tente novamente.');
    } finally {
      setShowDeleteModal(false);
      setCategoryToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setCategoryToDelete(null);
  };

  return (
    <Container>
      <div className="sheet-container" data-testid="categories-page">
        <div className="sheet-header">
          <h5 className="sheet-title">Categorias</h5>
          <div className="sheet-toolbar">
            <Button 
              className="excel-btn"
              data-testid="new-category-button"
              onClick={() => navigate('/categories/new')}
            >
              <FaPlus className="me-1" /> Nova Categoria
            </Button>
          </div>
        </div>
        
        <div className="p-3">
          <div className="mb-3">
            <Row className="g-3">
              <Col md={2}>
                <Form.Group>
                  <Form.Label>ID</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Buscar por ID"
                    value={filters.id}
                    onChange={(e) => handleFilterChange('id', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Nome</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Buscar por nome"
                    value={filters.name}
                    onChange={(e) => handleFilterChange('name', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Tipo</Form.Label>
                  <CategoryTypeSelect
                    value={filters.type}
                    onChange={(e) => handleFilterChange('type', e.target.value)}
                    showAllOption={true}
                    allOptionText="Todos os tipos"
                  />
                </Form.Group>
              </Col>
              <Col md={2} className="d-flex align-items-end">
                <Button
                  variant="outline-secondary"
                  onClick={clearFilters}
                  className="w-100"
                >
                  Limpar Filtros
                </Button>
              </Col>
            </Row>
          </div>

          <Table className="excel-table" data-testid="categories-table">
            <thead>
              <tr>
                <th style={{ width: '80px' }}>ID</th>
                <th>Nome</th>
                <th>Tipo</th>
                <th style={{ width: '120px' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredCategories.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center" data-testid="empty-message">
                    {loading ? 'Carregando...' : 'Nenhuma categoria encontrada'}
                  </td>
                </tr>
              ) : (
                filteredCategories.map((category) => (
                  <tr key={category.id} data-testid="category-row">
                    <td>{category.id}</td>
                    <td data-testid="category-name">{category.name}</td>
                    <td data-testid="category-type">{getCategoryTypeLabel(category.type)}</td>
                    <td>
                      <div className="d-flex gap-2">
                        <Button 
                          variant="outline-primary" 
                          size="sm"
                          className="me-2"
                          data-testid="edit-button"
                          onClick={() => handleEdit(category.id)}
                        >
                          <FaEdit />
                        </Button>
                        <Button 
                          variant="outline-danger" 
                          size="sm"
                          data-testid="delete-button"
                          onClick={() => handleDelete(category.id)}
                        >
                          <FaTrash />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
      </div>
      
      <ConfirmModal
        show={showDeleteModal}
        onHide={cancelDelete}
        onConfirm={confirmDelete}
        title="Excluir Categoria"
        message="Tem certeza que deseja excluir esta categoria?"
        confirmText="Excluir"
        cancelText="Cancelar"
      />
    </Container>
  );
};

export default Categories; 